import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { mapDetails } from './referenceFormHelpers';
import { Button } from '../../design-system/primitives/Button/Button';
import { Combobox } from '../../design-system/primitives/Combobox/Combobox';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import {
  centresApi,
  type Centre,
  type CentreInput,
  type City,
  type Dispensary,
} from '../../lib/api/reference';
import styles from './ReferenceFormDialog.module.css';
import { referenceName } from '../../lib/format/referenceName';

/**
 * The create/edit form for a centre — the bottom of the hierarchy, and now
 * the ONE place a dispensary meets a city.
 *
 * TWO INDEPENDENT PICKERS, DISPENSARY AND CITY. Before cities became global
 * (the API's `make_cities_global` migration) this form offered a city alone
 * and the server derived the dispensary from it — so the dispensary was
 * never visible here at all, which is exactly what the product owner
 * reported ("not even when creating centre!"). A city is now one row per
 * real place with no owner, so the dispensary has to be CHOSEN; both travel
 * on the wire as UUIDs (`dispensary_id`/`city_id`, `CentreRequestBase`).
 * Neither picker narrows the other: any dispensary may open a centre in any
 * city, and the first centre a dispensary opens in a city is precisely what
 * makes that city one it "serves".
 *
 * Both option lists are supplied by the caller (`CentreList.tsx`), ACTIVE
 * ones only: placing a new centre under a retired dispensary or city is not
 * a choice this form offers. `Combobox`, which opens as a dropdown on click
 * and filters as you type — both lists are long.
 */

export interface CentreFormDialogProps {
  open: boolean;
  node?: Centre;
  dispensaries: readonly Dispensary[];
  cities: readonly City[];
  onClose: () => void;
  onSaved: (node: Centre) => void;
}

interface FormState {
  dispensaryUuid: string | null;
  cityUuid: string | null;
  externalCode: string;
  nameEn: string;
  nameAr: string;
  place: string;
  directions: string;
  googleMapUrl: string;
  isActive: boolean;
}

function stateFor(node: Centre | undefined): FormState {
  if (node === undefined) {
    return {
      dispensaryUuid: null,
      cityUuid: null,
      externalCode: '',
      nameEn: '',
      nameAr: '',
      place: '',
      directions: '',
      googleMapUrl: '',
      isActive: true,
    };
  }
  return {
    dispensaryUuid: node.dispensary_uuid,
    cityUuid: node.city_uuid,
    externalCode: node.external_code,
    nameEn: node.name_en,
    nameAr: node.name_ar,
    place: node.place ?? '',
    directions: node.directions ?? '',
    googleMapUrl: node.google_map_url ?? '',
    isActive: node.is_active,
  };
}

type MissingField = 'dispensary_id' | 'city_id' | 'external_code' | 'name_en' | 'name_ar';

const FORBIDDEN_CODE = 'forbidden';

export function CentreFormDialog({
  open,
  node,
  dispensaries,
  cities,
  onClose,
  onSaved,
}: CentreFormDialogProps) {
  const { t, i18n } = useTranslation();
  const isEdit = node !== undefined;

  const [state, setState] = useState<FormState>(() => stateFor(node));
  const [missing, setMissing] = useState<readonly MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const inFlight = useRef(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(stateFor(node));
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
  }, [open, node]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('reference.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('reference.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('reference.actionFailed');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: MissingField[] = [];
    if (state.dispensaryUuid === null || state.dispensaryUuid === '') empty.push('dispensary_id');
    if (state.cityUuid === null || state.cityUuid === '') empty.push('city_id');
    if (state.externalCode.trim() === '') empty.push('external_code');
    if (state.nameEn.trim() === '') empty.push('name_en');
    if (state.nameAr.trim() === '') empty.push('name_ar');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      // Both non-null: the check above already returned when either was not.
      const input: CentreInput = {
        dispensary_id: state.dispensaryUuid as string,
        city_id: state.cityUuid as string,
        external_code: state.externalCode.trim(),
        name_en: state.nameEn.trim(),
        name_ar: state.nameAr.trim(),
        place: state.place.trim() === '' ? null : state.place.trim(),
        directions: state.directions.trim() === '' ? null : state.directions.trim(),
        google_map_url: state.googleMapUrl.trim() === '' ? null : state.googleMapUrl.trim(),
        is_active: state.isActive,
      };
      const saved =
        isEdit && node !== undefined
          ? await centresApi.update(node.uuid, input)
          : await centresApi.create(input);
      onSaved(saved);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  const dialogTitle =
    isEdit && node !== undefined
      ? t('reference.editCentreTitle', { name: node.name_en })
      : t('reference.createCentreTitle');

  const dispensaryOptions = withCurrent(
    dispensaries.map((d) => ({ value: d.uuid, label: referenceName(d, i18n.language) })),
    node?.dispensary ?? null,
    i18n.language,
  );
  const cityOptions = withCurrent(
    cities.map((c) => ({ value: c.uuid, label: referenceName(c, i18n.language) })),
    node?.city ?? null,
    i18n.language,
  );

  return (
    <Dialog open={open} size="lg" onClose={onClose} initialFocusRef={codeRef} title={dialogTitle}>
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div
          className={styles['failure']}
          role="alert"
          aria-atomic="true"
          data-testid="centreFormFailure"
        >
          {generalFailure}
        </div>

        <Combobox
          label={t('reference.fieldDispensary')}
          width="fill"
          required
          options={dispensaryOptions}
          value={state.dispensaryUuid}
          emptyMessage={t('reference.noParentsYet')}
          error={
            missing.includes('dispensary_id')
              ? t('reference.required')
              : fieldErrors['dispensary_id']
          }
          onChange={(value) => {
            setState((current) => ({ ...current, dispensaryUuid: value }));
          }}
        />

        <Combobox
          label={t('reference.fieldCity')}
          width="fill"
          required
          options={cityOptions}
          value={state.cityUuid}
          emptyMessage={t('reference.noCitiesYet')}
          error={missing.includes('city_id') ? t('reference.required') : fieldErrors['city_id']}
          onChange={(value) => {
            setState((current) => ({ ...current, cityUuid: value }));
          }}
        />

        <Input
          ref={codeRef}
          label={t('reference.fieldCode')}
          width="code"
          dir="ltr"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required
          value={state.externalCode}
          error={
            missing.includes('external_code')
              ? t('reference.required')
              : fieldErrors['external_code']
          }
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, externalCode: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameEn')}
          width="fill"
          dir="ltr"
          required
          value={state.nameEn}
          error={missing.includes('name_en') ? t('reference.required') : fieldErrors['name_en']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameEn: value }));
          }}
        />

        <Input
          label={t('reference.fieldNameAr')}
          width="fill"
          dir="rtl"
          required
          value={state.nameAr}
          error={missing.includes('name_ar') ? t('reference.required') : fieldErrors['name_ar']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, nameAr: value }));
          }}
        />

        <Input
          label={t('reference.fieldPlace')}
          width="fill"
          value={state.place}
          error={fieldErrors['place']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, place: value }));
          }}
        />

        <Input
          label={t('reference.fieldDirections')}
          className={styles['wide']}
          width="fill"
          value={state.directions}
          error={fieldErrors['directions']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, directions: value }));
          }}
        />

        <Input
          label={t('reference.fieldMapUrl')}
          className={styles['wide']}
          width="fill"
          dir="ltr"
          value={state.googleMapUrl}
          error={fieldErrors['google_map_url']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, googleMapUrl: value }));
          }}
        />

        <label className={styles['checkboxRow']}>
          <input
            type="checkbox"
            className={styles['checkbox']}
            checked={state.isActive}
            onChange={(event) => {
              const { checked } = event.target;
              setState((current) => ({ ...current, isActive: checked }));
            }}
          />
          {t('reference.fieldActive')}
        </label>

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting} icon={<FloppyDiskIcon size={16} />}>
            {t('reference.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('reference.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/**
 * The option list, plus the centre's CURRENT parent when the active-only
 * catalogue does not contain it (an edit of a centre whose dispensary or
 * city has since been deactivated). Without this the picker would show a
 * blank value for a field the centre really has, and a save that never
 * touched it would read as "required" — the form must not silently demand
 * a re-parent nobody asked for.
 */
function withCurrent(
  options: readonly { value: string; label: string }[],
  current: { uuid: string; name_en: string; name_ar: string } | null,
  language: string,
): readonly { value: string; label: string }[] {
  if (current === null || options.some((option) => option.value === current.uuid)) return options;
  return [...options, { value: current.uuid, label: referenceName(current, language) }];
}
